import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { flushSync } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import OversellAuthorizationDialog from './OversellAuthorizationDialog';
import { formatBackendTimestamps } from 'utils/errorHandling';
import { bookingService } from 'api/services/bookingService';
import { configService } from 'api/services/configService';
import type { Booking, BookingAdjustmentRequest, BookingDetailLineRequest, CreateBookingRequest, UpdateBookingRequest, BookingGuestRequest } from 'api/types/booking.types';
import type { AvailabilityResponse } from 'api/types/availability.types';
import PaymentCaptureModal from 'components/modals/modules/payment-module/PaymentCaptureModal';
import StayDetailsSection, { type StayData } from './sections/StayDetailsSection';
import RoomRateSection, { type DetailLineUI } from './sections/RoomRateSection';
import { makeCommercialRowKey, priceCommercialLine } from './sections/CommercialLinesSection';
import type { CommercialLineRow } from './sections/CommercialLinesSection';
import GuestSection from './sections/GuestSection';
import SourceChannelSection, { type SourceData } from './sections/SourceChannelSection';
import AdditionalDetailsSection, { type AdditionalData } from './sections/AdditionalDetailsSection';
import AdvanceSection from './sections/AdvanceSection';
import DiscountSection, { type DiscountData } from './sections/DiscountSection';
import AddonsSection, { type AddonRow, makeAddonRowKey } from './sections/AddonsSection';
import BookingSummaryPanel from './sections/BookingSummaryPanel';
import { isGuestNameRequired, calcLineByModel, BOOKING_SOURCE } from './booking.utils';
import { useAdvancePolicy, useApprovalRules, useBlockingPolicy, usePricingModel } from 'hooks/api/useConfig';
import { agentService } from 'api/services/agentService';
import { corporateService } from 'api/services/corporateService';
import { otaService } from 'api/services/otaService';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type { StayData, DetailLineUI, SourceData, AdditionalData };

export interface BookingFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record?: Booking | null;
  onSave: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => void;
  initialStayData?: Partial<StayData>;
  /**
   * Fires after a successful create (not edit) with the new booking's id and
   * landed status code. Used by the Front Office walk-in flow to chain straight
   * into Quick Check-In when the booking lands CONFIRMED (E2).
   */
  onSaved?: (bookingId: string, bookingStatusCode?: string) => void;
  /**
   * RBAC actions on booking-list. Only `oversell` is read here: it decides
   * whether an availability refusal offers the "accept the shortage" path or
   * just reports it. Fail-closed, and the server re-checks the permission
   * regardless — a client that lies about it gains nothing.
   */
  allowedActions?: string[];
}

// ============================================================================
// INITIAL DATA
// ============================================================================

const INITIAL_STAY: StayData = {
  propertyId: '', propertyName: '', propertyCode: '',
  checkInDate: '', checkOutDate: '',
};

const INITIAL_SOURCE: SourceData = {
  bookingSource: 'Direct', agentId: '', agentName: '',
  corporateId: '', corporateName: '', otaId: '', otaName: '', otaBookingRef: '',
  settlementMode: 'PARTY',
};

const INITIAL_ADDITIONAL: AdditionalData = {
  isVip: false, isComplimentary: false, complimentaryReason: '',
  specialNotes: '', expectedArrivalTime: '', advanceDueDate: '', advanceRequired: 0,
  advanceOverrideRemarks: '',
};

const INITIAL_DISCOUNT: DiscountData = {
  discountType: '', discountValue: 0, discountRemarks: '',
};

const INITIAL_GUEST: BookingGuestRequest = {
  guestType: 'Adult', title: '', firstName: '', lastName: '',
  isChargeable: true, isPrimary: true,
};

// ============================================================================
// E1 PARTIAL UPGRADE — line split / merge helpers
// ============================================================================

/**
 * Expands one card line into 1–2 request lines. A partial upgrade
 * (0 < upgradeRooms < numberOfRooms) becomes base + upgraded lines with the
 * SAME booked category and rate. Pax fill the base line's capacity first
 * (each line keeps ≥1 adult); overflow extras land on the upgraded line — the
 * totals are identical to the unsplit calculation because both lines share the
 * booked category's rate, base occupancy and rate plan.
 */
/**
 * Turns the UI lines' agreed rates into the booking's `adjustments[]`.
 *
 * Scope is addressed by POSITION into the REQUEST detail lines, and one UI
 * line can expand into two request lines (partial upgrade). An adjustment is
 * therefore emitted per resulting request line carrying the same agreed unit
 * rate — the server multiplies the unit delta by each line's own quantity, so
 * the totals come out exactly right either way.
 */
const buildAdjustments = (
  uiLines: DetailLineUI[],
  owners: number[],
  addonRows: AddonRow[],
): BookingAdjustmentRequest[] => {
  const out: BookingAdjustmentRequest[] = [];

  uiLines.forEach((line, uiIdx) => {
    const requestIdxs = owners
      .map((owner, i) => (owner === uiIdx ? i : -1))
      .filter(i => i >= 0);
    if (requestIdxs.length === 0) return;

    const remarks = (line.adjustmentRemarks || '').trim() || undefined;
    const components: [string, number | undefined, number][] = [
      ['ROOM', line.agreedRatePerRoom, line.ratePerRoom],
      ['MEAL', line.agreedMealAdultRate, line.mealAdultRate],
      ['EXTRA_PERSON', line.agreedExtraAdultRate, line.extraAdultRate],
    ];

    components.forEach(([componentCode, agreed, rack]) => {
      if (agreed == null || agreed === rack) return;
      requestIdxs.forEach(i => out.push({
        componentCode,
        adjustmentType: 'AgreedRate',
        detailLineIndex: i,
        agreedUnitRate: agreed,
        remarks,
      }));
    });
  });

  addonRows.forEach((row, i) => {
    if (row.agreedUnitAmount == null || row.agreedUnitAmount === row.unitAmount) return;
    out.push({
      componentCode: 'ADDON',
      adjustmentType: 'AgreedRate',
      addonIndex: i,
      agreedUnitRate: row.agreedUnitAmount,
      remarks: (row.adjustmentRemarks || '').trim() || undefined,
    });
  });

  return out;
};

/**
 * Line identity for lines loaded from the server. Separate counter from the
 * one in RoomRateSection only because the two modules cannot share module state;
 * both produce values that are unique within a single open form, which is all
 * the key has to guarantee.
 */
let hydratedLineKeySeq = 0;

/** Server commercial lines → editable rows. Copy mode drops ids so the clone creates fresh rows. */
const hydrateCommercialLines = (
  lines: import('api/types/booking.types').BookingCommercialLine[] | undefined,
  dropIds: boolean,
): CommercialLineRow[] =>
  (lines ?? []).map((c) => ({
    _rowKey: makeCommercialRowKey(),
    id: dropIds ? undefined : c.id,
    lineNo: c.lineNo,
    description: c.description,
    numAdults: c.numAdults,
    numChildren: c.numChildren,
    ratePerPax: c.ratePerPax,
    mealPlanId: c.mealPlanId,
    mealPlanCode: c.mealPlanCode ?? undefined,
    mealAdultRate: c.mealAdultRate,
    mealChildRate: c.mealChildRate,
  }));
const makeHydratedLineKey = () => `hy-${++hydratedLineKeySeq}`;

const toRequestLines = (l: DetailLineUI): BookingDetailLineRequest[] => {
  const shared = {
    roomCategoryId: l.roomCategoryId,
    ratePlanId: l.ratePlanId,
    ratePlanDetailId: l.ratePlanDetailId,
    mealPlanId: l.mealPlanId,
    ratePerRoom: l.ratePerRoom,
    extraAdultRate: l.extraAdultRate,
    extraChildRate: l.extraChildRate,
    mealAdultRate: l.mealAdultRate,
    mealChildRate: l.mealChildRate,
    includeTaxes: l.includeTaxes ?? false,
    // lineKey is deliberately NOT forwarded — it is form-local identity only.
    rateBasis: l.rateBasis,
    ratePerPax: l.ratePerPax,
    occupancyRemark: l.occupancyRemark,
  };
  const upgradeRooms = Math.min(l.upgradeRooms ?? 0, l.numberOfRooms);
  if (upgradeRooms <= 0 || !l.allocatedRoomCategoryId) {
    return [{ ...shared, numberOfRooms: l.numberOfRooms, numAdults: l.numAdults, numChildren: l.numChildren }];
  }
  if (upgradeRooms >= l.numberOfRooms) {
    return [{
      ...shared, numberOfRooms: l.numberOfRooms, numAdults: l.numAdults, numChildren: l.numChildren,
      allocatedRoomCategoryId: l.allocatedRoomCategoryId,
    }];
  }
  const baseRooms = l.numberOfRooms - upgradeRooms;
  const baseCap = (l.baseOccupancy || 2) * baseRooms;
  const baseAdults = Math.max(1, Math.min(l.numAdults - 1, baseCap));
  const baseChildren = Math.min(l.numChildren, Math.max(0, baseCap - baseAdults));
  return [
    { ...shared, numberOfRooms: baseRooms, numAdults: baseAdults, numChildren: baseChildren },
    {
      ...shared, numberOfRooms: upgradeRooms,
      numAdults: l.numAdults - baseAdults, numChildren: l.numChildren - baseChildren,
      allocatedRoomCategoryId: l.allocatedRoomCategoryId,
    },
  ];
};

/**
 * Reverse of toRequestLines for edit/copy loads: a stored partial upgrade is
 * two detail lines (same booked category + rate plan, exactly one allocated).
 * Merge them back into one card line with upgradeRooms so the form shows the
 * booking exactly as it was entered.
 */
const mergeSplitLines = (lines: DetailLineUI[]): DetailLineUI[] => {
  const out: DetailLineUI[] = [];
  for (const l of lines) {
    const mate = out.find(o =>
      o.roomCategoryId === l.roomCategoryId
      && o.ratePlanId === l.ratePlanId
      && o.mealPlanId === l.mealPlanId
      && o.ratePerRoom === l.ratePerRoom
      // Occupancy lines in one category share a rack rate, so without this two
      // DIFFERENT sharing arrangements would merge into one on reload.
      && o.ratePerPax === l.ratePerPax
      && o.occupancyRemark === l.occupancyRemark
      && !!o.allocatedRoomCategoryId !== !!l.allocatedRoomCategoryId);
    if (!mate) {
      out.push({ ...l, upgradeRooms: l.allocatedRoomCategoryId ? l.numberOfRooms : undefined });
      continue;
    }
    const upgradedRooms = l.allocatedRoomCategoryId ? l.numberOfRooms : mate.numberOfRooms;
    const allocatedId = l.allocatedRoomCategoryId ?? mate.allocatedRoomCategoryId;
    const allocatedName = l.allocatedCategoryName ?? mate.allocatedCategoryName;
    mate.numberOfRooms += l.numberOfRooms;
    mate.numAdults += l.numAdults;
    mate.numChildren = (mate.numChildren ?? 0) + (l.numChildren ?? 0);
    mate.upgradeRooms = upgradedRooms;
    mate.allocatedRoomCategoryId = allocatedId;
    mate.allocatedCategoryName = allocatedName;
  }
  return out;
};

// ============================================================================
// COMPONENT
// ============================================================================

const BookingFormModal: React.FC<BookingFormModalProps> = ({
  isOpen, mode, record, onSave, onClose,
  tabId, moduleName, createInstanceId, copyInstanceId,
  minimizeRef, onRestoreModal, initialStayData, onSaved,
  allowedActions = [],
}) => {
  const { minimize } = useModalMinimize();

  // --- Form data ---
  const [stayData, setStayData] = useState<StayData>({ ...INITIAL_STAY });
  const [detailLines, setDetailLines] = useState<DetailLineUI[]>([]);
  /**
   * Per-pax commercial state (CRS-Per-Pax-Commercial-Allocation-Design): the
   * priced lines and the stated room count. Owned here because the payload is
   * built here; RoomRateSection renders them. Empty for per-room bookings, and
   * every branch below is gated on that emptiness so per-room is untouched.
   */
  const [commercialLines, setCommercialLines] = useState<CommercialLineRow[]>([]);
  const [totalRoomsInput, setTotalRoomsInput] = useState('');
  const [guests, setGuests] = useState<BookingGuestRequest[]>([{ ...INITIAL_GUEST }]);
  const [linkedGuestId, setLinkedGuestId] = useState('');
  const [sourceData, setSourceData] = useState<SourceData>({ ...INITIAL_SOURCE });
  const [additionalData, setAdditionalData] = useState<AdditionalData>({ ...INITIAL_ADDITIONAL });
  const [discountData, setDiscountData] = useState<DiscountData>({ ...INITIAL_DISCOUNT });
  const [discountExpanded, setDiscountExpanded] = useState(false);
  // Ancillary add-ons (spa, flowers, etc.) attached to this booking.
  // Sit OUTSIDE the discount pipeline — see BookingService.cs / AddonsSection.
  const [addons, setAddons] = useState<AddonRow[]>([]);
  const [addonsExpanded, setAddonsExpanded] = useState(false);
  // Advance section expanded by default — it's the most-touched section after
  // source/stay/room and the override chip needs to be visible without an extra click.
  const [advanceExpanded, setAdvanceExpanded] = useState(true);
  // Tracks whether the user has manually edited the discount this session — used to
  // avoid clobbering it when the corporate-prefill effect runs on subsequent corporate
  // changes.
  const discountManuallyEditedRef = useRef(false);
  // Tracks whether the cashier has manually edited the advance amount or due
  // date this session. Drives whether the auto-recalc effect can reset advance
  // to the new policy when totals/discount change. Cleared on full reset.
  const advanceManuallyEditedRef = useRef(false);
  const [availabilityResult, setAvailabilityResult] = useState<AvailabilityResponse | null>(null);

  // --- UI state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  // Advance capture modal — only available in edit mode (record exists)
  const [advanceModalOpen, setAdvanceModalOpen] = useState(false);
  const [saveAsConfirmed, setSaveAsConfirmed] = useState(false);
  // When true (set by the "Save & Block" button), CreateAsync runs first to land
  // the booking as TENTATIVE, then a follow-up POST /Booking/{id}/block transitions
  // it to BLOCKED. Initial expiry = property-level `block_duration_hours` config
  // (24h fallback). Extensions (when allowed) are capped separately by
  // `max_block_hold_hours`; that cap does NOT apply to the first hold.
  const [saveAsBlocked, setSaveAsBlocked] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [loadingFullBooking, setLoadingFullBooking] = useState(false);
  // Group-booking warning threshold (large_booking_warning_rooms, BOOKING_RULES).
  // Warning only — there is no hard room-count limit; availability + upgrades
  // are the real constraint. Null until loaded / when config is unavailable.
  const [largeBookingThreshold, setLargeBookingThreshold] = useState<number | null>(null);
  // Backdated entry window (backdated_booking_max_days, BOOKING_RULES) — how
  // many days in the past the check-in date may be set. 0 = disallowed
  // (fail-safe when the config is missing; the server enforces the same).
  const [backdatedMaxDays, setBackdatedMaxDays] = useState<number>(0);

  // --- Concurrency conflict state ---
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  /**
   * An availability refusal the user may be able to authorise past. Kept apart
   * from concurrencyConflict because a 409 means two very different things —
   * someone else edited the row, or the rooms ran out — and only the second has
   * a way forward.
   */
  const [oversellPrompt, setOversellPrompt] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  /**
   * An availability refusal the user may NOT authorise past — no `oversell`
   * permission, or the property/cap/cutoff closed the door. Reported as a plain
   * dead end rather than through concurrencyConflict, whose two buttons both
   * lose the edit: "Load Latest" overwrites the form from the server and "Keep
   * My Changes" only closes the dialog. Neither retries the save, so a shortage
   * dressed up as a concurrency conflict reads as a bug.
   */
  const [availabilityBlock, setAvailabilityBlock] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  /**
   * Carries the authorisation into the retry. A ref rather than state because
   * the retry calls the same save closure immediately — a state update would not
   * be visible in it yet.
   */
  const oversellAcceptedRef = useRef<{ accepted: boolean; reason: string }>({ accepted: false, reason: '' });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const effectiveRecordRef = useRef<Booking | null | undefined>(record);

  // --- Refs for minimize ---
  const modeRef = useRef(mode);
  const recordRef = useRef(record);
  const createInstanceIdRef = useRef(createInstanceId);
  const copyInstanceIdRef = useRef(copyInstanceId);
  const isRestoreRef = useRef(false);
  const initialSnapshotRef = useRef('');
  const dataLoadedRef = useRef(false);

  modeRef.current = mode;
  recordRef.current = record;
  createInstanceIdRef.current = createInstanceId;
  copyInstanceIdRef.current = copyInstanceId;

  // --- Snapshot for dirty detection ---
  const buildSnapshot = useCallback(() => {
    // Strip the client-only `_rowKey` from add-on rows so dirty detection
    // tracks only the persistent fields, not transient React keys.
    const addonsSnapshot = addons.map(({ _rowKey: _k, ...rest }) => rest);
    // Same stripping for commercial lines: hydration mints fresh _rowKeys per
    // open, so including them would read every reopened booking as dirty.
    const commercialSnapshot = commercialLines.map(({ _rowKey: _k, ...rest }) => rest);
    return JSON.stringify({
      stayData, detailLines, guests, linkedGuestId, sourceData, additionalData, discountData,
      addons: addonsSnapshot, commercialLines: commercialSnapshot, totalRooms: totalRoomsInput,
    });
  }, [stayData, detailLines, guests, linkedGuestId, sourceData, additionalData, discountData, addons,
      commercialLines, totalRoomsInput]);

  // --- Computed ---
  const numNights = stayData.checkInDate && stayData.checkOutDate
    ? Math.max(0, Math.round((new Date(stayData.checkOutDate).getTime() - new Date(stayData.checkInDate).getTime()) / 86400000))
    : 0;

  const guestName = useMemo(() => {
    const primary = guests.find(g => g.isPrimary);
    if (!primary) return '';
    return [primary.title, primary.firstName, primary.lastName].filter(Boolean).join(' ').trim();
  }, [guests]);

  // --- Child ages from guest list (for age-based extra charge calculation) ---
  const childAges = useMemo(() =>
    guests
      .filter(g => g.guestType === 'Child' || g.guestType === 'Infant')
      .map(g => g.guestType === 'Infant' ? (g.age ?? 0) : g.age),
    [guests],
  );

  // --- Pricing model from config (property-level: 'a_la_carte' | 'bundled') ---
  const { pricingModel, refetch: refetchPricingModel } = usePricingModel(
    stayData.propertyId || undefined,
  );

  // --- Approval rules from config (high_discount_threshold + comp_approval_required) ---
  const { config: approvalRules, refetch: refetchApprovalRules } = useApprovalRules(
    stayData.propertyId || undefined,
  );

  // --- Subtotal (gross, before discount). Mirrors BookingSummaryPanel. ---
  const subtotal = useMemo(() => {
    // Per-pax: the commercial lines ARE the money; the detail lines are only
    // the allocation and would price to zero. Everything downstream — discount,
    // net, advance — hangs off this figure, so the branch lives at the root.
    if (commercialLines.length > 0) {
      return commercialLines.reduce(
        (sum, l) => sum + priceCommercialLine(l, Math.max(1, numNights)).amount, 0);
    }
    let total = 0;
    for (const line of detailLines) {
      total += calcLineByModel(pricingModel, line, numNights, childAges).lineTotal;
    }
    return total;
  }, [commercialLines, detailLines, numNights, childAges, pricingModel]);

  // --- Component-wise rate adjustments (mirror of BookingAdjustmentCalculator).
  //     Standard subtotal above stays RACK-based; this is the signed delta. ---
  const { adjustmentTotal, adjustmentPremium, adjustmentDiscount } = useMemo(() => {
    let premium = 0;
    let discount = 0;
    const add = (agreed: number | undefined, rack: number, qty: number) => {
      if (agreed == null || agreed === rack || qty <= 0) return;
      const amt = Math.round((agreed - rack) * qty * 100) / 100;
      if (amt >= 0) premium += amt; else discount += -amt;
    };
    for (const line of detailLines) {
      // Quantities come from the pricing calculator — the same numbers the card's
      // adjustment panel and the server use. Inferring them here made the Booking
      // Summary disagree with the card sitting next to it: under bundled pricing the
      // meal is billed flat on base_occupancy x rooms, so `numAdults x nights` gave 3
      // where the charge used 4, and a -200 line total was summarised as -150.
      const charges = calcLineByModel(pricingModel, line, numNights, childAges);
      add(line.agreedRatePerRoom, line.ratePerRoom, charges.roomUnits);
      add(line.agreedMealAdultRate, line.mealAdultRate, charges.mealAdultUnits);
      add(line.agreedExtraAdultRate, line.extraAdultRate, charges.extraAdultUnits);
    }
    for (const row of addons) {
      if (!row.addonId || row.agreedUnitAmount == null) continue;
      const qty = Math.max(1, row.quantity);
      const amt = Math.round((row.agreedUnitAmount - row.unitAmount) * qty * 100) / 100;
      if (amt >= 0) premium += amt; else discount += -amt;
    }
    return { adjustmentTotal: premium - discount, adjustmentPremium: premium, adjustmentDiscount: discount };
  }, [detailLines, addons, numNights, childAges, pricingModel]);

  /** Selling subtotal — what the guest is actually charged before discount. */
  const sellingSubtotal = useMemo(() => subtotal + adjustmentTotal, [subtotal, adjustmentTotal]);

  // --- Discount + net + approval state (mirror of BookingService.BuildBookingTotalsAsync) ---
  const { discountAmount, netAmount, requiresApproval, approvalReasons } = useMemo(() => {
    // Comp wins.
    if (additionalData.isComplimentary) {
      const compFires = approvalRules.compApprovalRequired;
      return {
        discountAmount: subtotal,
        netAmount: 0,
        requiresApproval: compFires,
        approvalReasons: compFires ? 'Complimentary booking — approval required by config.' : '',
      };
    }
    // Manual discount.
    let dAmt = 0;
    if (discountData.discountType && discountData.discountValue > 0 && subtotal > 0) {
      const raw = discountData.discountType === 'Percentage'
        ? subtotal * (discountData.discountValue / 100)
        : discountData.discountValue;
      dAmt = Math.min(Math.max(0, raw), subtotal);
    }
    const net = Math.max(0, subtotal + adjustmentTotal - dAmt);
    // OVERALL discount — the booking discount plus every component discount,
    // netted against premiums (mirrors BookingDiscountCalculator §3.4). A
    // net-premium booking can never trip the threshold.
    const overallDiscount = Math.max(0, dAmt - adjustmentTotal);
    const pct = subtotal > 0 ? (overallDiscount / subtotal) * 100 : 0;

    // Lowest minimum rate among rate plans actually used in this booking.
    let lowestMinRate = 0;
    if (availabilityResult?.categories?.length) {
      const ratePlanIds = new Set(detailLines.map(l => l.ratePlanId).filter((x): x is string => !!x));
      const allPlans = availabilityResult.categories.flatMap(c => c.ratePlans || []);
      const used = allPlans.filter(rp => ratePlanIds.has(rp.ratePlanId) && rp.minimumRate > 0);
      if (used.length > 0) lowestMinRate = Math.min(...used.map(rp => rp.minimumRate));
    }

    const totalRoomsAll = detailLines.reduce((s, l) => s + (l.numberOfRooms || 0), 0);
    const ratePerRoomNight = totalRoomsAll > 0 && numNights > 0
      ? net / (totalRoomsAll * numNights)
      : 0;

    const reasons: string[] = [];
    // Gate mirrors BookingService/BookingDiscountCalculator: the %-threshold
    // rule only fires while discount_approval_required is ON. The min-rate
    // floor below stays independent.
    if (approvalRules.discountApprovalRequired
        && overallDiscount > 0 && pct >= approvalRules.highDiscountThreshold) {
      reasons.push(`Overall discount ${pct.toFixed(2)}% ≥ ${approvalRules.highDiscountThreshold}% threshold.`);
    }
    if (lowestMinRate > 0 && ratePerRoomNight < lowestMinRate) {
      reasons.push(`Net rate ₹${ratePerRoomNight.toFixed(2)}/room/night below minimum ₹${lowestMinRate.toFixed(2)}.`);
    }
    // Per-line offered rate vs that line's own plan minimum — the other half
    // of the minimum-permissible-rate trigger (server: AnyLineBelowMinimumRate).
    if (availabilityResult?.categories?.length) {
      const allPlans = availabilityResult.categories.flatMap(c => c.ratePlans || []);
      for (const l of detailLines) {
        const plan = allPlans.find(rp => rp.ratePlanId === l.ratePlanId && rp.minimumRate > 0);
        if (!plan) continue;
        const offered = l.agreedRatePerRoom ?? l.ratePerRoom;
        if (offered < plan.minimumRate) {
          reasons.push(`Offered rate ₹${offered.toFixed(2)} is below the plan minimum ₹${plan.minimumRate.toFixed(2)}.`);
          break;
        }
      }
    }
    // E1 room upgrade — mirrors BookingService.CheckRoomUpgradeApprovalAsync.
    // Without this reason the banner stays silent while the server still
    // routes the booking to PENDING_APPROVAL (ROOM_UPGRADE request).
    if (approvalRules.roomUpgradeRequiresApproval) {
      const upgradedRooms = detailLines.reduce(
        (s, l) => s + (l.allocatedRoomCategoryId && (l.upgradeRooms ?? 0) > 0 ? (l.upgradeRooms ?? 0) : 0), 0);
      if (upgradedRooms > 0) {
        reasons.push(`Room upgrade × ${upgradedRooms} requires approval per property policy.`);
      }
    }
    return {
      discountAmount: dAmt,
      netAmount: net,
      requiresApproval: reasons.length > 0,
      approvalReasons: reasons.join(' '),
    };
  }, [subtotal, additionalData.isComplimentary, discountData.discountType, discountData.discountValue,
      approvalRules, availabilityResult, detailLines, numNights]);

  // Treat netAmount as the "grandTotal" for downstream advance-policy calc — comp drops to 0.
  const grandTotal = netAmount;

  // --- Corporate auto-populate: prefill discount from tbl_corporate when source = Corporate ---
  useEffect(() => {
    if (!isOpen) return;
    if (sourceData.bookingSource !== BOOKING_SOURCE.CORPORATE) return;
    if (!sourceData.corporateId) return;
    if (discountManuallyEditedRef.current) return;
    let cancelled = false;
    corporateService.getById(sourceData.corporateId).then(res => {
      if (cancelled || !res?.data) return;
      const c = res.data;
      const type = (c.discountType === 'Percentage' || c.discountType === 'FlatAmount')
        ? c.discountType
        : '';
      if (!type || !c.discountValue || c.discountValue <= 0) return;
      setDiscountData(prev => ({
        ...prev,
        discountType: type,
        discountValue: c.discountValue ?? 0,
      }));
      setDiscountExpanded(true);
    }).catch(() => {/* swallow — corporate prefill is best-effort */});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sourceData.bookingSource, sourceData.corporateId]);

  // --- Advance policy from config ---
  const firstRatePlanId = detailLines.length > 0 ? detailLines[0].ratePlanId : undefined;
  const { config: advanceConfig, refetch: refetchAdvancePolicy } = useAdvancePolicy(
    stayData.propertyId || undefined,
    firstRatePlanId || undefined,
  );
  // Drives the default expiry on the "Save & Block" path (replaces the previous
  // hardcoded 24h). Cap is also enforced server-side on /Booking/{id}/block.
  const { config: blockingConfig, refetch: refetchBlockingPolicy } = useBlockingPolicy(
    stayData.propertyId || undefined,
  );

  // --- Source eligibility (Agent/Corporate/OTA flagged is_credit_eligible) ---
  // Mirrors backend BookingService.IsCreditEligibleAsync. The masters carry the
  // flag but the SyutSelect option doesn't, so we fetch the relevant entity by
  // id whenever the picked source changes. Used to render the eligibility banner
  // and to suppress the override-loose chip on the eligible-bypass branch.
  const [isSourceEligible, setIsSourceEligible] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isOpen) return;
      let eligible = false;
      try {
        if (sourceData.agentId) {
          const r = await agentService.getById(sourceData.agentId);
          if ((r as any)?.data?.isCreditEligible || (r as any)?.isCreditEligible) eligible = true;
        }
        if (!eligible && sourceData.corporateId) {
          const r = await corporateService.getById(sourceData.corporateId);
          if ((r as any)?.data?.isCreditEligible || (r as any)?.isCreditEligible) eligible = true;
        }
        if (!eligible && sourceData.otaId) {
          const r = await otaService.getById(sourceData.otaId);
          if ((r as any)?.data?.isCreditEligible || (r as any)?.isCreditEligible) eligible = true;
        }
      } catch { /* ignore — banner just stays in default (non-eligible) state */ }
      if (!cancelled) setIsSourceEligible(eligible);
    })();
    return () => { cancelled = true; };
  }, [isOpen, sourceData.agentId, sourceData.corporateId, sourceData.otaId]);

  // ---------------------------------------------------------------------------
  // Advance-policy projection (mirrors backend AdvanceConfirmationRules.Resolve)
  // ---------------------------------------------------------------------------
  // Plain JS replica of the backend rules, used to power:
  //   - the eligibility banner (green / blue / yellow under the modal title),
  //   - the override-loose chip + mandatory remarks in AdditionalDetailsSection,
  //   - the Save confirmation dialog wording.
  // The server is the source of truth — these are projections so the cashier
  // sees the resulting status BEFORE clicking Save.
  const policyAmount = useMemo(() => {
    if (!advanceConfig || netAmount <= 0) return 0;
    return Math.round(netAmount * (advanceConfig.advancePercent / 100) * 100) / 100;
  }, [advanceConfig, netAmount]);

  const policyDueDate = useMemo(() => {
    if (!advanceConfig) return '';
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(today);
    due.setDate(due.getDate() + advanceConfig.advanceDueDays);
    if (stayData.checkInDate) {
      const ci = new Date(stayData.checkInDate); ci.setHours(0, 0, 0, 0);
      if (due > ci) due.setTime(today.getTime());
    }
    if (due < today) due.setTime(today.getTime());
    return `${due.getFullYear()}-${String(due.getMonth() + 1).padStart(2, '0')}-${String(due.getDate()).padStart(2, '0')}`;
  }, [advanceConfig, stayData.checkInDate]);

  // Eligible cashier with NO operator entry → eligibility-bypass branch (no advance).
  // Eligible cashier WITH operator entry → normal workflow + override-loose checks apply.
  // "Opt-in" means the cashier MANUALLY typed in an advance value/date (tracked via
  // advanceManuallyEditedRef); auto-filled values from the policy effect don't count
  // as opt-in, otherwise an eligible source's auto-fill would defeat the bypass.
  const operatorOptedIntoAdvance = useMemo(
    () => advanceManuallyEditedRef.current
      && ((additionalData.advanceRequired ?? 0) > 0 || !!additionalData.advanceDueDate),
    [additionalData.advanceRequired, additionalData.advanceDueDate]
  );
  const eligibleBypass = isSourceEligible && !operatorOptedIntoAdvance;
  const isComplimentaryBooking = additionalData.isComplimentary || netAmount <= 0;

  const isOverrideLoose = useMemo(() => {
    if (isComplimentaryBooking) return false;       // Rule 5 — comp short-circuits
    if (isSourceEligible)       return false;       // Eligible source — any voluntarily-entered advance is strengthening
    if (policyAmount <= 0)      return false;       // Defensive — no policy to compare against
    const amountLoose = (additionalData.advanceRequired ?? 0) < policyAmount;
    const dueLoose = additionalData.advanceDueDate && policyDueDate
      ? new Date(additionalData.advanceDueDate) > new Date(policyDueDate)
      : false;
    return amountLoose || dueLoose;
  }, [isComplimentaryBooking, isSourceEligible, policyAmount, policyDueDate,
      additionalData.advanceRequired, additionalData.advanceDueDate]);

  // Banner state derives from the same chain. Yellow > Blue > Green > none.
  type BannerKind = 'eligible-bypass' | 'eligible-opted-in' | 'override-loose' | null;
  const eligibilityBanner: BannerKind = useMemo(() => {
    if (!isSourceEligible && !isOverrideLoose) return null;
    if (isOverrideLoose)  return 'override-loose';
    if (eligibleBypass)   return 'eligible-bypass';
    if (isSourceEligible) return 'eligible-opted-in';
    return null;
  }, [isSourceEligible, eligibleBypass, isOverrideLoose]);

  // Combined approval projection — used by Save buttons / dialog wording so any
  // approval trigger (discount, comp, override-loose) lands with the same UX:
  // disable "Save & Confirm", show "Save Pending Approval" labels, etc.
  const requiresApprovalAny = requiresApproval || isOverrideLoose;

  // --- Auto-calculate advance fields when config/totals/dates change ---
  useEffect(() => {
    if (!advanceConfig || !isOpen) return;
    // Skip auto-calc for edit mode (preserve existing values) and on restore
    if (mode === 'edit' || isRestoreRef.current) return;

    // Skip auto-fill entirely for credit-eligible sources — the eligibility
    // bypass means no advance is required by default. The cashier can still
    // type a value to opt in to collecting one (Rule 1), but pre-filling it
    // would (a) make the displayed advance misleading and (b) trip the
    // override-loose detection the moment the user clears it back to 0.
    if (isSourceEligible) {
      // Also clear any previously auto-filled values when the cashier picks
      // an eligible source AFTER the form opened with a non-eligible default.
      if (!advanceManuallyEditedRef.current
          && (additionalData.advanceRequired > 0 || additionalData.advanceDueDate)) {
        setAdditionalData(prev => ({ ...prev, advanceRequired: 0, advanceDueDate: '' }));
      }
      return;
    }

    const updates: Partial<AdditionalData> = {};

    // Auto-calculate advance due date: today + advanceDueDays (semantics changed
    // April 2026 — was check-in - days, now booking-date + days). Clamp to today
    // (never before booking date) and to check-in (never after check-in).
    if (stayData.checkInDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueDate = new Date(today);
      dueDate.setDate(dueDate.getDate() + advanceConfig.advanceDueDays);
      const checkIn = new Date(stayData.checkInDate);
      checkIn.setHours(0, 0, 0, 0);
      if (dueDate > checkIn) dueDate.setTime(today.getTime());
      if (dueDate < today)   dueDate.setTime(today.getTime());
      const y = dueDate.getFullYear();
      const m = String(dueDate.getMonth() + 1).padStart(2, '0');
      const d = String(dueDate.getDate()).padStart(2, '0');
      updates.advanceDueDate = `${y}-${m}-${d}`;
    }

    // Auto-populate advance amount to track the policy on the post-discount net
    // — but only when the cashier hasn't manually edited it. The previous
    // "< minAdvance" guard caused stale highs to linger when discount lowered
    // the net (e.g. discount → net drops → policy drops → but advance stayed
    // at the old higher value because old > new minimum).
    if (grandTotal > 0 && !advanceManuallyEditedRef.current) {
      const policy = grandTotal * (advanceConfig.advancePercent / 100);
      updates.advanceRequired = Math.round(policy * 100) / 100;
    }

    if (Object.keys(updates).length > 0) {
      setAdditionalData(prev => ({ ...prev, ...updates }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advanceConfig, stayData.checkInDate, grandTotal, isOpen, mode, isSourceEligible]);

  // --- Initialize form on open ---
  useEffect(() => {
    if (!isOpen) return;
    effectiveRecordRef.current = record;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      // Re-fetch advance + blocking policy + pricing model on restore — all can change while minimized
      if (stayData.propertyId) {
        refetchAdvancePolicy(stayData.propertyId, firstRatePlanId);
        refetchBlockingPolicy(stayData.propertyId);
        refetchPricingModel(stayData.propertyId);
        refetchApprovalRules(stayData.propertyId);
      }
      return;
    }

    // Reset
    setFieldErrors({});
    setAvailabilityResult(null);
    setDetailsExpanded(false);
    dataLoadedRef.current = false;

    if ((mode === 'edit' || mode === 'copy') && record) {
      setStayData({
        propertyId: record.propertyId,
        propertyName: record.propertyName,
        propertyCode: record.propertyCode,
        checkInDate: record.checkInDate ? record.checkInDate.split('T')[0] : '',
        checkOutDate: record.checkOutDate ? record.checkOutDate.split('T')[0] : '',
      });
      setSourceData({
        bookingSource: record.bookingSource || 'Direct',
        agentId: record.agentId || '',
        agentName: record.agentName || '',
        corporateId: record.corporateId || '',
        corporateName: record.corporateName || '',
        otaId: (record as any).otaId || '',
        otaName: (record as any).otaDisplayName || record.otaName || '',
        otaBookingRef: record.otaBookingRef || '',
        settlementMode: record.settlementFollowsParty ? 'PARTY' : (record.settlementMode || 'GUEST'),
      });
      setAdditionalData({
        isVip: record.isVip ?? false,
        isComplimentary: record.isComplimentary ?? false,
        complimentaryReason: record.complimentaryReason || '',
        specialNotes: record.specialNotes || '',
        expectedArrivalTime: record.expectedArrivalTime || '',
        advanceDueDate: record.advanceDueDate ? record.advanceDueDate.split('T')[0] : '',
        advanceRequired: record.advanceRequired ?? 0,
        // Remarks aren't persisted on the booking row (they live on the
        // ADVANCE_OVERRIDE approval row's metadata). Edit always starts blank.
        advanceOverrideRemarks: '',
      });
      // Hydrate discount fields. For complimentary bookings the backend stored
      // discountType=null even though discount_amount=subtotal — leave the manual
      // inputs empty (the locked Discount section reflects the comp state).
      setDiscountData({
        discountType: (record.discountType as 'Percentage' | 'FlatAmount' | null) || '',
        discountValue: record.discountValue ?? 0,
        discountRemarks: record.discountRemarks || '',
      });
      // Treat hydrated values as user-edited so corporate-prefill doesn't clobber them.
      discountManuallyEditedRef.current = !!(record.discountType || record.discountValue);
      // Same for advance: an existing booking already has a settled value; the
      // auto-recalc effect should leave it alone unless the cashier types in it.
      advanceManuallyEditedRef.current = !!(record.advanceRequired && record.advanceRequired > 0);
      if (record.discountAmount && record.discountAmount > 0) setDiscountExpanded(true);
      setLinkedGuestId(record.guestId || '');
      setDetailLines([]);
      setCommercialLines([]); setTotalRoomsInput('');
      setGuests([{ ...INITIAL_GUEST }]);

      // Auto-expand sections with data
      if (record.isVip || record.isComplimentary || record.specialNotes) setDetailsExpanded(true);

      // Re-fetch advance + blocking policy + pricing model in case config changed since last load
      if (record.propertyId) {
        refetchAdvancePolicy(record.propertyId);
        refetchBlockingPolicy(record.propertyId);
        refetchPricingModel(record.propertyId);
        refetchApprovalRules(record.propertyId);
      }

      // Fetch full booking
      if (record.bookingId) {
        setLoadingFullBooking(true);
        bookingService.getFullBooking(record.bookingId).then(res => {
          if (res.data) {
            const full = res.data;
            // Refresh the row-version snapshot with the value the server holds
            // right now. Without this, `effectiveRecordRef.current.rowVersion`
            // stays frozen at whatever the list returned when the user opened
            // the modal — so if any other tab / user / background job updates
            // this booking while the modal is open, Save fails with a
            // concurrency error even though the fresher value was already
            // fetched here.
            if (full.booking) {
              effectiveRecordRef.current = full.booking;
            }
            if (full.detailLines) {
              // Rate adjustments come back as separate rows keyed by detail-line
              // id. Rehydrate them onto the UI lines as agreed rates — without
              // this, saving an edit would silently wipe the negotiation.
              const adjByLine = new Map<string, { room?: number; meal?: number; extra?: number; remarks?: string }>();
              (full.adjustments ?? []).forEach(a => {
                if (!a.bookingDetailId || a.agreedUnitRate == null) return;
                const entry = adjByLine.get(a.bookingDetailId) ?? {};
                if (a.componentCode === 'ROOM') entry.room = a.agreedUnitRate;
                else if (a.componentCode === 'MEAL') entry.meal = a.agreedUnitRate;
                else if (a.componentCode === 'EXTRA_PERSON') entry.extra = a.agreedUnitRate;
                if (a.remarks) entry.remarks = a.remarks;
                adjByLine.set(a.bookingDetailId, entry);
              });

              setCommercialLines(hydrateCommercialLines(full.commercialLines, mode === 'copy'));
              setTotalRoomsInput(full.booking?.totalRooms ? String(full.booking.totalRooms) : '');
              setDetailLines(mergeSplitLines(full.detailLines.map(d => ({
                lineKey: makeHydratedLineKey(),
                rateBasis: d.rateBasis === 'PER_PAX' ? 'PER_PAX' as const : 'PER_ROOM' as const,
                ratePerPax: d.ratePerPax,
                occupancyRemark: d.occupancyRemark,
                agreedRatePerRoom: adjByLine.get(d.bookingDetailId)?.room,
                agreedMealAdultRate: adjByLine.get(d.bookingDetailId)?.meal,
                agreedExtraAdultRate: adjByLine.get(d.bookingDetailId)?.extra,
                adjustmentRemarks: adjByLine.get(d.bookingDetailId)?.remarks,
                roomCategoryId: d.roomCategoryId,
                categoryCode: d.categoryCode,
                categoryName: d.categoryName,
                allocatedRoomCategoryId: d.allocatedRoomCategoryId,
                allocatedCategoryName: d.allocatedCategoryName,
                numberOfRooms: d.numberOfRooms,
                ratePlanId: d.ratePlanId,
                ratePlanDetailId: d.ratePlanDetailId,
                ratePlanName: d.rateName || '',
                mealPlanId: d.mealPlanId,
                mealPlanCode: d.mealPlanCode || '',
                mealPlanName: d.mealPlanName || '',
                ratePerRoom: d.ratePerRoom,
                extraAdultRate: d.extraAdultRate ?? 0,
                extraChildRate: d.extraChildRate ?? 0,
                mealAdultRate: d.mealAdultRate ?? 0,
                mealChildRate: d.mealChildRate ?? 0,
                // Bundled-pricing snapshots — required for summary/splitup to reproduce
                // the stored totals on edit/view. Without these the client-side calc
                // would use default=0 and blow up the Meal bucket.
                pricingModel: d.pricingModel as 'a_la_carte' | 'bundled' | undefined,
                defaultMealPlanId: d.defaultMealPlanId,
                defaultMealAdultRate: d.defaultMealAdultRate ?? 0,
                defaultMealChildRate: d.defaultMealChildRate ?? 0,
                numAdults: d.numAdults,
                numChildren: d.numChildren ?? 0,
                // Must be the room category's base_occupancy (exposed by the view),
                // NOT numAdults — falling back to numAdults hides all extras on edit.
                baseOccupancy: d.baseOccupancy || 2,
                includeTaxes: d.includeTaxes ?? false,
              }))));
            }
            if (full.guests?.length) {
              setGuests(full.guests.map(g => ({
                guestType: g.guestType,
                title: g.title || '',
                firstName: g.firstName,
                lastName: g.lastName || '',
                dateOfBirth: g.dateOfBirth,
                age: g.age,
                isChargeable: g.isChargeable ?? true,
                isPrimary: g.isPrimary,
                guestId: g.guestId,
              })));
            }
            // Hydrate ancillary add-ons. Copy mode drops the existing row id so
            // the cloned booking creates fresh rows.
            if (full.addons && full.addons.length) {
              setAddons(full.addons.map(a => ({
                _rowKey: makeAddonRowKey(),
                id: mode === 'copy' ? undefined : a.id,
                addonId: a.addonId,
                addonLabel: `${a.addonCode} - ${a.addonName}`,
                unitAmount: a.unitAmount,
                quantity: a.quantity,
                serviceDate: a.serviceDate ? a.serviceDate.slice(0, 10) : undefined,
                pax: a.pax,
                remarks: a.remarks,
              })));
              setAddonsExpanded(true);
            } else {
              setAddons([]);
            }
          }
          // Set snapshot AFTER data loads (not setTimeout)
          dataLoadedRef.current = true;
        }).catch(() => {}).finally(() => setLoadingFullBooking(false));
      }
    } else {
      // Create mode
      const stay = initialStayData ? { ...INITIAL_STAY, ...initialStayData } : { ...INITIAL_STAY };
      setStayData(stay);
      setDetailLines([]);
      setCommercialLines([]); setTotalRoomsInput('');
      setGuests([{ ...INITIAL_GUEST }]);
      setLinkedGuestId('');
      setSourceData({ ...INITIAL_SOURCE });
      setAdditionalData({ ...INITIAL_ADDITIONAL });
      setDiscountData({ ...INITIAL_DISCOUNT });
      setAddons([]);
      setAddonsExpanded(false);
      discountManuallyEditedRef.current = false;
      advanceManuallyEditedRef.current = false;
    }

    // Snapshot for create mode (edit mode snapshots after data load)
    if (mode === 'create') {
      requestAnimationFrame(() => { initialSnapshotRef.current = buildSnapshot(); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // --- Set snapshot when edit data finishes loading ---
  useEffect(() => {
    if (dataLoadedRef.current) {
      dataLoadedRef.current = false;
      initialSnapshotRef.current = buildSnapshot();
    }
  }, [detailLines, guests, buildSnapshot]);

  // --- Dirty check ---
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return buildSnapshot() !== initialSnapshotRef.current;
  }, [buildSnapshot]);

  // --- Group-booking warning threshold + backdated window (config, property-scoped) ---
  useEffect(() => {
    if (!stayData.propertyId) { setLargeBookingThreshold(null); setBackdatedMaxDays(0); return; }
    let cancelled = false;
    configService
      .getEffectiveValue('large_booking_warning_rooms', [{ type: 'PROPERTY', id: stayData.propertyId }])
      .then((res) => {
        if (cancelled) return;
        const parsed = parseInt(res.data?.effectiveValue ?? '', 10);
        setLargeBookingThreshold(Number.isNaN(parsed) ? null : parsed);
      })
      .catch(() => { if (!cancelled) setLargeBookingThreshold(null); });
    configService
      .getEffectiveValue('backdated_booking_max_days', [{ type: 'PROPERTY', id: stayData.propertyId }])
      .then((res) => {
        if (cancelled) return;
        const parsed = parseInt(res.data?.effectiveValue ?? '', 10);
        setBackdatedMaxDays(Number.isNaN(parsed) ? 0 : Math.max(0, parsed));
      })
      .catch(() => { if (!cancelled) setBackdatedMaxDays(0); });
    return () => { cancelled = true; };
  }, [stayData.propertyId]);

  const totalRoomsRequested = useMemo(
    () => detailLines.reduce((sum, l) => sum + l.numberOfRooms, 0),
    [detailLines],
  );
  const largeBookingWarning =
    largeBookingThreshold !== null && totalRoomsRequested > largeBookingThreshold
      ? `⚠ This booking has ${totalRoomsRequested} rooms — please confirm this is an intended group booking. `
      : '';

  // Backdated entry (late / after-midnight bookings) — warn + confirm.
  const todayIso = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const isBackdated = !!stayData.checkInDate && stayData.checkInDate < todayIso;
  const backdatedWarning = isBackdated
    ? `⚠ BACKDATED booking — check-in ${stayData.checkInDate} is in the past. Inventory is consumed for the past nights and the booking appears in the Yesterday arrivals queue. `
    : '';

  // Edit re-price warning — dates/rooms/pax edits change money; make it explicit.
  const originalNet = mode === 'edit' ? (record?.netAmount ?? null) : null;
  const editRepriceWarning =
    mode === 'edit' && originalNet !== null && Math.abs(netAmount - originalNet) >= 0.01
      ? `⚠ This edit re-prices the stay: net ₹${netAmount.toLocaleString('en-IN')} (was ₹${originalNet.toLocaleString('en-IN')}). Advance and approval rules re-evaluate on the new values. `
      : '';

  // --- Validation ---
  const validateAll = useCallback((): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Stay
    if (!stayData.propertyId) errors.propertyId = 'Property is required';
    if (!stayData.checkInDate) errors.checkInDate = 'Check-in date is required';
    if (!stayData.checkOutDate) errors.checkOutDate = 'Check-out date is required';
    if (stayData.checkInDate && stayData.checkOutDate && new Date(stayData.checkOutDate) <= new Date(stayData.checkInDate)) {
      errors.checkOutDate = 'Check-out must be after check-in';
    }

    // Rooms
    if (detailLines.length === 0) errors.detailLines = 'At least one room is required';
    detailLines.forEach((line, i) => {
      if (!line.roomCategoryId) errors[`line${i}.category`] = 'Category required';
      if (line.numberOfRooms < 1) errors[`line${i}.rooms`] = 'At least 1 room';
      // Per-room-category guest count vs configured caps. The steppers already
      // enforce these in real time, but we validate again at save time as the
      // contract for the room-category masters: Max Adults / Max Children are
      // hard caps and Adults+Children must stay within Max Occupancy.
      const cat = availabilityResult?.categories.find(c => c.roomCategoryId === line.roomCategoryId);
      // Per-pax allocation rows carry no pax of their own (distributed from the
      // commercial totals at save) and no per-line rate — the card-model checks
      // below would reject them for reasons that no longer apply. Their own
      // rules live in the isPerPax block after this loop.
      if (cat && line.rateBasis === 'PER_PAX') {
        // `acceptShortage` is honoured here for consistency, though the per-pax
        // branch replaces the category cards with an allocation list and has no
        // control to set it yet — see the note in the design doc.
        if (line.numberOfRooms > cat.availableRooms && !line.acceptShortage) {
          errors['allocation'] =
            `${cat.categoryName}: only ${cat.availableRooms} available for these dates.`;
        }
      } else if (cat) {
        const rooms = Math.max(1, line.numberOfRooms);
        const adultsCap = Math.min(cat.maxAdults, cat.maxOccupancy) * rooms;
        const childrenCap = cat.maxChildren * rooms;
        const sumCap = cat.maxOccupancy * rooms;
        /* These are written to `line-<categoryId>` because that is the ONLY key the
           category card renders (see its errorMessage prop). The old `line<i>.adults`
           style keys were displayed nowhere at all, so exceeding occupancy blocked the
           save behind "Please fix the highlighted errors" with nothing highlighted.

           The commonest way in is reducing the room count: the pax stay where they
           were and silently become illegal for the smaller booking, so each message
           names the current value, the limit, the room count it derives from, and both
           ways out. */
        const roomWord = `${rooms} room${rooms !== 1 ? 's' : ''}`;
        const occupancyError =
          line.numAdults > adultsCap
            ? `${cat.categoryName}: ${line.numAdults} adults exceeds the limit of ${adultsCap} for ${roomWord}. `
              + `Reduce adults to ${adultsCap}, or add a room.`
          : line.numChildren > childrenCap
            ? `${cat.categoryName}: ${line.numChildren} children exceeds the limit of ${childrenCap} for ${roomWord}. `
              + `Reduce children to ${childrenCap}, or add a room.`
          : line.numAdults + line.numChildren > sumCap
            ? `${cat.categoryName}: ${line.numAdults + line.numChildren} guests exceeds the maximum occupancy of `
              + `${sumCap} for ${roomWord}. Reduce guests, or add a room.`
          : null;

        // Never clobber a shortage/upgrade message already on this card — that one is
        // about whether the rooms exist at all, which the guest count cannot fix.
        if (occupancyError && !errors[`line-${line.roomCategoryId}`]) {
          errors[`line-${line.roomCategoryId}`] = occupancyError;
        }

        // Upgrade split (E1 partial upgrade) — mirrors the card's inline checks.
        const upgradeRooms = Math.min(line.upgradeRooms ?? 0, line.numberOfRooms);
        const shortage = Math.max(0, line.numberOfRooms - cat.availableRooms);
        if (shortage > 0 && upgradeRooms < shortage && !line.acceptShortage) {
          // "Accept shortage" is the other legitimate answer, so the rule only
          // binds when the desk has not chosen it. The server still refuses the
          // save unless policy, cap, cutoff and permission all allow it.
          errors[`line-${line.roomCategoryId}`] =
            `${cat.categoryName}: only ${cat.availableRooms} available — upgrade at least ${shortage} room${shortage !== 1 ? 's' : ''}, or accept the shortage`;
        } else if (upgradeRooms > 0) {
          const target = line.allocatedRoomCategoryId
            ? availabilityResult?.categories.find(c => c.roomCategoryId === line.allocatedRoomCategoryId)
            : undefined;
          if (!target) {
            errors[`line-${line.roomCategoryId}`] = `${cat.categoryName}: choose the category to host the upgraded rooms`;
          } else if (upgradeRooms > target.availableRooms) {
            errors[`line-${line.roomCategoryId}`] =
              `${cat.categoryName}: ${target.categoryName} has only ${target.availableRooms} room${target.availableRooms !== 1 ? 's' : ''} available`;
          } else if (upgradeRooms < line.numberOfRooms && line.numAdults < 2) {
            // A partial upgrade splits into two lines and each needs at least one adult.
            errors[`line-${line.roomCategoryId}`] =
              `${cat.categoryName}: a partial upgrade needs at least 2 adults (one per room group)`;
          }
        }

      }
    });

    // ── Per-pax: the commercial lines are the money and the stated total is
    //    the contract. Mirrors the server (ValidateAllocationMatchesTotal and
    //    chk_commercial_line_pax) so a mismatch surfaces inline, not as a
    //    server error after the confirm dialog. ──
    const isPerPax = commercialLines.length > 0 || detailLines.some(l => l.rateBasis === 'PER_PAX');
    if (isPerPax) {
      if (commercialLines.length === 0) {
        errors.commercialLines = 'Add at least one priced line.';
      } else if (commercialLines.some(l => !l.description.trim())) {
        errors.commercialLines = 'Every line needs a description — it appears on the invoice.';
      } else if (commercialLines.some(l => (l.numAdults || 0) + (l.numChildren || 0) === 0)) {
        errors.commercialLines = 'A line with no adults or children prices nothing — set a headcount or remove it.';
      } else if (commercialLines.some(l => (l.ratePerPax || 0) <= 0)) {
        errors.commercialLines = 'Every line needs a rate above zero.';
      }
      const stated = parseInt(totalRoomsInput, 10) || 0;
      if (stated < 1) {
        errors.totalRooms = 'Enter the number of rooms the group requires.';
      } else {
        const allocated = detailLines.reduce((a, l) => a + (l.numberOfRooms || 0), 0);
        if (allocated !== stated && !errors.allocation) {
          errors.allocation = allocated < stated
            ? `Allocate ${stated - allocated} more room${stated - allocated !== 1 ? 's' : ''} — ${allocated} of ${stated} placed.`
            : `${allocated} rooms allocated but the booking is for ${stated}.`;
        }
      }
    }

    // Rooms are committed per CATEGORY, but a category can now carry several
    // occupancy lines — so the per-line shortage check above cannot see the
    // total. Without this, two 5-room lines would both pass against 8 available.
    const roomsByCategory = new Map<string, number>();
    /**
     * Categories where the desk has explicitly accepted the shortage. The
     * per-line rule above already skips those lines, but this total check ran
     * independently and re-raised the same refusal — which is what made
     * "Accept shortage" look broken: the save was blocked by an error keyed to
     * a category, and the only thing that error did to the screen was tint a
     * card border.
     */
    const shortageAccepted = new Set<string>();
    detailLines.forEach(line => {
      // Upgraded rooms are hosted elsewhere; they don't consume this category.
      const ownRooms = line.numberOfRooms - Math.min(line.upgradeRooms ?? 0, line.numberOfRooms);
      roomsByCategory.set(
        line.roomCategoryId,
        (roomsByCategory.get(line.roomCategoryId) ?? 0) + ownRooms,
      );
      if (line.acceptShortage) shortageAccepted.add(line.roomCategoryId);
    });
    roomsByCategory.forEach((rooms, categoryId) => {
      const cat = availabilityResult?.categories.find(c => c.roomCategoryId === categoryId);
      if (cat && rooms > cat.availableRooms
          && !shortageAccepted.has(categoryId)
          && !errors[`line-${categoryId}`]) {
        errors[`line-${categoryId}`] =
          `${cat.categoryName}: ${rooms} rooms requested across all occupancy lines, only ${cat.availableRooms} available`;
      }
    });

    // Guest — name required only for certain sources (Direct, Walk-In, Website)
    const primary = guests.find(g => g.isPrimary);
    if (primary && isGuestNameRequired(sourceData.bookingSource) && !primary.firstName.trim())
      errors.guestFirstName = 'Primary guest name is required';

    // Source
    if (sourceData.bookingSource === BOOKING_SOURCE.TRAVEL_AGENT && !sourceData.agentId) errors.agentId = 'Agent is required';
    if (sourceData.bookingSource === BOOKING_SOURCE.CORPORATE && !sourceData.corporateId) errors.corporateId = 'Corporate is required';
    if (sourceData.bookingSource === BOOKING_SOURCE.OTA) {
      if (!sourceData.otaId) errors.otaId = 'OTA is required';
      if (!sourceData.otaBookingRef.trim()) errors.otaBookingRef = 'OTA reference is required';
    }

    // Additional
    if (additionalData.isComplimentary && !additionalData.complimentaryReason.trim()) errors.complimentaryReason = 'Reason is required';

    // Discount validation — server enforces too, but fail fast in the UI
    if (!additionalData.isComplimentary && discountData.discountValue > 0) {
      if (!discountData.discountType) {
        errors.discountValue = 'Pick a discount type before entering a value';
      } else if (discountData.discountType === 'Percentage' && discountData.discountValue > 100) {
        errors.discountValue = 'Percentage discount cannot exceed 100%';
      }
      if (!discountData.discountRemarks.trim()) {
        errors.discountRemarks = 'Remarks are required when a discount is applied';
      }
    }

    // Override-loosening: remarks are mandatory when the operator entry is
    // looser than policy (lower amount or later due date). Mirrors backend
    // AdvanceConfirmationRules.OverrideRemarksRequiredMessage.
    if (isOverrideLoose && !additionalData.advanceOverrideRemarks.trim()) {
      errors.advanceOverrideRemarks = 'Remarks are required when the entered advance is looser than policy';
    }

    return errors;
  }, [stayData, detailLines, guests, sourceData, additionalData, discountData, isOverrideLoose, availabilityResult,
      // Per-pax rules read these — undeclared, validation runs on stale lines.
      commercialLines, totalRoomsInput]);

  // --- Close ---
  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  // --- Save ---
  // mode: 'block'   → save TENTATIVE then POST /block with 24h expiry
  //       'confirm' → save & confirm (subject to backend approval gating)
  const handleSave = useCallback((mode: 'block' | 'confirm') => {
    const errors = validateAll();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Auto-expand collapsed sections with errors
      if (errors.advanceRequired || errors.advanceOverrideRemarks || errors.advanceDueDate) setAdvanceExpanded(true);
      if (errors.complimentaryReason || errors.discountValue || errors.discountRemarks) setDiscountExpanded(true);
      notificationService.toast.error('Please fix the highlighted errors.');
      return;
    }
    setFieldErrors({});
    // When approval is required, the booking can never be saved as CONFIRMED nor
    // BLOCKED — the backend will override to PENDING_APPROVAL anyway. Force both
    // flags off so the confirm-dialog labels match what actually happens on save.
    setSaveAsConfirmed(requiresApprovalAny ? false : mode === 'confirm');
    setSaveAsBlocked(requiresApprovalAny ? false : mode === 'block');
    setSaveConfirmOpen(true);
  }, [validateAll, requiresApprovalAny]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    // The desk already declared it is carrying a shortage on at least one line,
    // so ask for the reason now rather than letting the save round-trip, fail,
    // and re-open the same dialog. Skipped once the authorisation is in hand —
    // that is the retry coming back through here.
    // Totalled per CATEGORY, not per line — a category can carry several
    // occupancy lines, and two 1-room lines against 1 available are a shortage
    // that no single line shows. Judged per line, this would skip the reason
    // prompt and then be refused by the server for having no authorisation.
    const acceptedByCategory = new Map<string, { name: string; rooms: number; available: number }>();
    detailLines.forEach((l) => {
      if (!l.acceptShortage) return;
      const cat = availabilityResult?.categories.find(c => c.roomCategoryId === l.roomCategoryId);
      if (!cat) return;
      const own = l.numberOfRooms - Math.min(l.upgradeRooms ?? 0, l.numberOfRooms);
      const entry = acceptedByCategory.get(l.roomCategoryId)
        ?? { name: cat.categoryName, rooms: 0, available: cat.availableRooms };
      entry.rooms += own;
      acceptedByCategory.set(l.roomCategoryId, entry);
    });

    const shortCategories = Array.from(acceptedByCategory.values())
      .filter((c) => c.rooms > c.available);

    if (shortCategories.length > 0 && !oversellAcceptedRef.current.accepted) {
      const short = shortCategories
        .map((c) => {
          const n = c.rooms - c.available;
          return `${c.name} by ${n} room${n !== 1 ? 's' : ''}`;
        })
        .join(', ');
      setIsSaving(false);
      setOversellPrompt({
        open: true,
        message: `This booking commits beyond available inventory: ${short}.`,
      });
      return;
    }

    try {
      // Expand UI lines into request lines while remembering which UI line each
      // came from — rate adjustments are addressed by REQUEST-line position and
      // a partial upgrade splits one UI line into two.
      const requestDetailLines: BookingDetailLineRequest[] = [];
      const requestLineOwners: number[] = [];
      detailLines.forEach((uiLine, uiIdx) => {
        toRequestLines(uiLine).forEach(rl => {
          requestDetailLines.push(rl);
          requestLineOwners.push(uiIdx);
        });
      });

      // Per-pax: allocation rows carry no pax of their own — distribute the
      // commercial headcount proportionally by rooms, last line taking the
      // remainder so nothing is lost to rounding. Occupancy reporting reads
      // line pax, so zeros here would understate the house.
      const isPerPaxSave = commercialLines.length > 0;
      if (isPerPaxSave && requestDetailLines.length > 0) {
        const totAdults = commercialLines.reduce((a, l) => a + (l.numAdults || 0), 0);
        const totChildren = commercialLines.reduce((a, l) => a + (l.numChildren || 0), 0);
        const totRooms = requestDetailLines.reduce((a, l) => a + (l.numberOfRooms || 0), 0);
        let remAdults = totAdults; let remChildren = totChildren; let remRooms = totRooms;
        requestDetailLines.forEach((l) => {
          const last = remRooms === (l.numberOfRooms || 0);
          const la = last ? remAdults
            : Math.min(remAdults, Math.round(totAdults * ((l.numberOfRooms || 0) / Math.max(1, totRooms))));
          const lc = last ? remChildren
            : Math.min(remChildren, Math.round(totChildren * ((l.numberOfRooms || 0) / Math.max(1, totRooms))));
          l.numAdults = la; l.numChildren = lc;
          remAdults -= la; remChildren -= lc; remRooms -= (l.numberOfRooms || 0);
        });
      }

      const payload: CreateBookingRequest = {
        // Only ever set by an explicit confirmation in the oversell dialog —
        // never a default, never remembered across saves.
        acceptOversell: oversellAcceptedRef.current.accepted || undefined,
        oversellReason: oversellAcceptedRef.current.reason || undefined,
        propertyId: stayData.propertyId,
        bookingSource: sourceData.bookingSource,
        guestId: linkedGuestId || undefined,
        agentId: sourceData.agentId || undefined,
        corporateId: sourceData.corporateId || undefined,
        otaId: sourceData.otaId || undefined,
        otaName: sourceData.otaName || undefined,
        otaBookingRef: sourceData.otaBookingRef || undefined,
        settlementMode: sourceData.settlementMode,
        checkInDate: stayData.checkInDate,
        checkOutDate: stayData.checkOutDate,
        expectedArrivalTime: additionalData.expectedArrivalTime || undefined,
        specialNotes: additionalData.specialNotes || undefined,
        isVip: additionalData.isVip,
        isComplimentary: additionalData.isComplimentary,
        complimentaryReason: additionalData.complimentaryReason || undefined,
        advanceDueDate: additionalData.advanceDueDate || undefined,
        advanceRequired: additionalData.advanceRequired || undefined,
        // Only sent when override-loosens; backend rejects loose overrides without remarks.
        advanceOverrideRemarks: isOverrideLoose
          ? (additionalData.advanceOverrideRemarks.trim() || undefined)
          : undefined,
        // Discount payload — backend ignores discountType/Value for complimentary bookings
        // (forces 100% waiver) but still records discountRemarks for audit.
        discountType: additionalData.isComplimentary
          ? undefined
          : (discountData.discountType || undefined),
        discountValue: additionalData.isComplimentary
          ? undefined
          : (discountData.discountValue && discountData.discountValue > 0 ? discountData.discountValue : undefined),
        discountRemarks: additionalData.isComplimentary
          ? undefined  // backend folds complimentaryReason into discount_remarks
          : (discountData.discountRemarks || undefined),
        // E1: a partial upgrade expands into base + upgraded lines (same booked
        // category and rate); full/no upgrade stays a single line.
        detailLines: requestDetailLines,
        // Per-pax only: the priced lines and the stated room count. When present
        // the server prices from these and validates the allocation sum; absent,
        // a per-room booking behaves exactly as before.
        commercialLines: isPerPaxSave
          ? commercialLines.map(({ _rowKey, ...l }, i) => ({ ...l, lineNo: i + 1 }))
          : undefined,
        totalRooms: isPerPaxSave ? (parseInt(totalRoomsInput, 10) || undefined) : undefined,
      adjustments: buildAdjustments(detailLines, requestLineOwners, addons.filter(a => a.addonId)),
        guests: guests.filter(g => g.isPrimary || g.firstName.trim()).map(g => ({
          guestType: g.guestType,
          title: g.title || undefined,
          firstName: g.firstName.trim(),
          lastName: g.lastName || undefined,
          dateOfBirth: g.dateOfBirth || undefined,
          age: g.age,
          isChargeable: g.isChargeable,
          isPrimary: g.isPrimary,
          guestId: g.guestId,
          phone: g.phone?.trim() || undefined,
          email: g.email?.trim() || undefined,
        })),
        // Drop blank rows (no addon selected) and strip client-only fields.
        addons: addons
          .filter(a => a.addonId)
          .map(a => ({
            id: a.id,
            addonId: a.addonId,
            unitAmount: Math.max(0, Number(a.unitAmount) || 0),
            quantity: Math.max(1, Number(a.quantity) || 1),
            remarks: a.remarks?.trim() || undefined,
          })),
        saveAsConfirmed,
      };

      let savedBookingId: string | undefined;
      let savedStatusCode: string | undefined;
      if (mode === 'edit') {
        const eff = effectiveRecordRef.current!;
        await bookingService.update(eff.bookingId, { ...payload, rowVersion: eff.rowVersion } as UpdateBookingRequest);
        notificationService.toast.success(`Booking "${eff.bookingNumber}" updated successfully.`);
      } else {
        const created = await bookingService.create(payload);
        // "Save & Block" path: chase the create with a /block call so the booking
        // lands as BLOCKED. Initial expiry comes from the property-level
        // `block_duration_hours` config (24h fallback). Extensions (when policy
        // allows) bring `max_block_hold_hours` into play later — not here.
        // Skipped when the booking landed as PENDING_APPROVAL (block transition is
        // only valid from TENTATIVE).
        const newBookingId = created.data?.bookingId || (created.data as any)?.id;
        const landedAsTentative = (created.data?.bookingStatusCode || '').toUpperCase() === 'TENTATIVE';
        savedBookingId = newBookingId;
        savedStatusCode = created.data?.bookingStatusCode;
        if (saveAsBlocked && newBookingId && landedAsTentative) {
          const blockHours = blockingConfig?.blockDurationHours ?? 24;
          const expiresAt = new Date();
          expiresAt.setHours(expiresAt.getHours() + blockHours);
          await bookingService.block(newBookingId, expiresAt.toISOString());
          notificationService.toast.success(`Booking created and blocked (${blockHours}h hold).`);
        } else if ((created.data?.bookingStatusCode || '').toUpperCase() === 'PENDING_APPROVAL') {
          const num = created.data?.bookingNumber;
          notificationService.toast.info(
            `Booking${num ? ` "${num}"` : ''} created and sent for approval — track it in the Approval Inbox. It will confirm once approved.`,
            6000,
          );
        } else {
          notificationService.toast.success('Booking created successfully.');
        }
      }

      setIsSaving(false);
      onSave();
      if (savedBookingId) onSaved?.(savedBookingId, savedStatusCode);
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message;
      // The API tags an availability conflict with 'RoomAvailability' in the
      // errors array, so this does not depend on matching message text.
      const isAvailabilityConflict =
        Array.isArray(err?.response?.data?.errors)
        && err.response.data.errors.includes('RoomAvailability');

      if (status === 409 && backendMessage && isAvailabilityConflict) {
        // An inventory refusal is never a concurrency conflict: nobody else
        // touched the row, the rooms are simply not there. Routed apart from
        // concurrencyConflict on the `RoomAvailability` tag alone — whether the
        // user can act on it only decides WHICH dialog, never that a shortage
        // may be reported as someone else's edit.
        if (allowedActions.includes('oversell')) {
          // The server already decided this shortage is potentially sellable by
          // someone; it will re-check policy, cap, cutoff and permission on the
          // retry, so a refusal here is still authoritative.
          setOversellPrompt({ open: true, message: backendMessage });
        } else {
          setAvailabilityBlock({ open: true, message: backendMessage });
        }
      } else if (status === 409 && backendMessage) {
        setConcurrencyConflict({ open: true, message: formatBackendTimestamps(backendMessage) });
      } else {
        notificationService.toast.error(backendMessage || 'An error occurred. Please try again.');
      }
    }
  }, [stayData, sourceData, additionalData, discountData, detailLines, guests, linkedGuestId, saveAsConfirmed, saveAsBlocked, blockingConfig, isOverrideLoose, mode, onSave, onSaved,
      // The payload sends these — undeclared, a save ships a stale rate sheet.
      // availabilityResult backs the pre-save shortage check — stale, and the
      // reason dialog would name the wrong categories.
      commercialLines, totalRoomsInput, allowedActions, availabilityResult,
      // `addons` feeds BOTH the addons[] payload and the ADDON-scope
      // adjustments. Undeclared, this closure keeps whatever the rows were at
      // the last OTHER change, so an add-on entered as the final edit before
      // Save — the usual order, the section sits near the bottom — is dropped
      // and the booking saves without it.
      addons]);

  // --- Refresh booking from server after concurrency conflict ---
  const handleRefreshFromConflict = useCallback(async () => {
    const eff = effectiveRecordRef.current;
    if (!eff) {
      setConcurrencyConflict({ open: false, message: '' });
      return;
    }
    setIsRefreshing(true);
    try {
      const response = await bookingService.getFullBooking(eff.bookingId);
      const full = response.data;
      if (full && full.booking) {
        const b = full.booking;
        setStayData({
          propertyId: b.propertyId,
          propertyName: b.propertyName,
          propertyCode: b.propertyCode,
          checkInDate: b.checkInDate ? b.checkInDate.split('T')[0] : '',
          checkOutDate: b.checkOutDate ? b.checkOutDate.split('T')[0] : '',
        });
        setSourceData({
          bookingSource: b.bookingSource || 'Direct',
          agentId: b.agentId || '',
          agentName: b.agentName || '',
          corporateId: b.corporateId || '',
          corporateName: b.corporateName || '',
          otaId: (b as any).otaId || '',
          otaName: (b as any).otaDisplayName || b.otaName || '',
          otaBookingRef: b.otaBookingRef || '',
          settlementMode: b.settlementFollowsParty ? 'PARTY' : (b.settlementMode || 'GUEST'),
        });
        setAdditionalData({
          isVip: b.isVip ?? false,
          isComplimentary: b.isComplimentary ?? false,
          complimentaryReason: b.complimentaryReason || '',
          specialNotes: b.specialNotes || '',
          expectedArrivalTime: b.expectedArrivalTime || '',
          advanceDueDate: b.advanceDueDate ? b.advanceDueDate.split('T')[0] : '',
          advanceRequired: b.advanceRequired ?? 0,
          advanceOverrideRemarks: '',
        });
        setLinkedGuestId(b.guestId || '');
        if (full.detailLines) {
          setCommercialLines(hydrateCommercialLines(full.commercialLines, false));
          setTotalRoomsInput(full.booking?.totalRooms ? String(full.booking.totalRooms) : '');
          setDetailLines(mergeSplitLines(full.detailLines.map(d => ({
            lineKey: makeHydratedLineKey(),
            rateBasis: d.rateBasis === 'PER_PAX' ? 'PER_PAX' as const : 'PER_ROOM' as const,
            ratePerPax: d.ratePerPax,
            occupancyRemark: d.occupancyRemark,
            roomCategoryId: d.roomCategoryId,
            categoryCode: d.categoryCode,
            categoryName: d.categoryName,
            allocatedRoomCategoryId: d.allocatedRoomCategoryId,
            allocatedCategoryName: d.allocatedCategoryName,
            numberOfRooms: d.numberOfRooms,
            ratePlanId: d.ratePlanId,
            ratePlanDetailId: d.ratePlanDetailId,
            ratePlanName: d.rateName || '',
            mealPlanId: d.mealPlanId,
            mealPlanCode: d.mealPlanCode || '',
            mealPlanName: d.mealPlanName || '',
            ratePerRoom: d.ratePerRoom,
            extraAdultRate: d.extraAdultRate ?? 0,
            extraChildRate: d.extraChildRate ?? 0,
            mealAdultRate: d.mealAdultRate ?? 0,
            mealChildRate: d.mealChildRate ?? 0,
            // Bundled-pricing snapshots — see matching comment in the initial-load block above.
            pricingModel: d.pricingModel as 'a_la_carte' | 'bundled' | undefined,
            defaultMealPlanId: d.defaultMealPlanId,
            defaultMealAdultRate: d.defaultMealAdultRate ?? 0,
            defaultMealChildRate: d.defaultMealChildRate ?? 0,
            numAdults: d.numAdults,
            numChildren: d.numChildren ?? 0,
            // Must be the room category's base_occupancy (exposed by the view),
            // NOT numAdults — falling back to numAdults hides all extras on edit.
            baseOccupancy: d.baseOccupancy || 2,
            includeTaxes: d.includeTaxes ?? false,
          }))));
        }
        if (full.guests?.length) {
          setGuests(full.guests.map(g => ({
            guestType: g.guestType,
            title: g.title || '',
            firstName: g.firstName,
            lastName: g.lastName || '',
            dateOfBirth: g.dateOfBirth,
            age: g.age,
            isChargeable: g.isChargeable ?? true,
            isPrimary: g.isPrimary,
            guestId: g.guestId,
          })));
        }
        effectiveRecordRef.current = b;
        // Snapshot will refresh via the dataLoadedRef effect — set flag
        dataLoadedRef.current = true;
        setFieldErrors({});
        notificationService.toast.success('Loaded the latest version. Please review and save.');
      }
      setConcurrencyConflict({ open: false, message: '' });
    } catch (err: any) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message ||
        'Failed to load the latest version.';
      notificationService.toast.error(backendMessage);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // --- Minimize ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentMode = modeRef.current;
    const curCreateId = createInstanceIdRef.current;
    const curCopyId = copyInstanceIdRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${curCreateId || 'default'}`;
    } else if (currentMode === 'copy') {
      modalId = `${tabId}-form-modal-copy-${curCopyId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${recordRef.current?.bookingId || 'new'}`;
    }

    const idx = currentMode === 'copy' ? curCopyId || '1' : curCreateId || '1';
    let title: string;
    if (currentMode === 'edit') {
      title = `Edit - ${recordRef.current?.bookingNumber || 'Booking'}`;
    } else if (currentMode === 'copy') {
      title = guestName ? `Copy - ${guestName} (${idx})` : `Copy (${idx})`;
    } else {
      title = guestName ? `Add - ${guestName} (${idx})` : `Add New (${idx})`;
    }

    // Capture all state
    const savedStay = { ...stayData };
    const savedLines = [...detailLines];
    // Per-pax draft: the priced lines and the stated total ARE the booking —
    // restoring the allocation without them would resurrect a group at ₹0.
    const savedCommercial = [...commercialLines];
    const savedTotalRooms = totalRoomsInput;
    const savedGuests = [...guests];
    const savedLinkedGuestId = linkedGuestId;
    const savedSource = { ...sourceData };
    const savedAdditional = { ...additionalData };
    const savedAvail = availabilityResult;
    const savedDetailsExpanded = detailsExpanded;
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? buildSnapshot() !== savedSnapshot : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        flushSync(() => {
          isRestoreRef.current = true;
          setStayData(savedStay);
          setDetailLines(savedLines);
          setCommercialLines(savedCommercial);
          setTotalRoomsInput(savedTotalRooms);
          setGuests(savedGuests);
          setLinkedGuestId(savedLinkedGuestId);
          setSourceData(savedSource);
          setAdditionalData(savedAdditional);
          setAvailabilityResult(savedAvail);
          setDetailsExpanded(savedDetailsExpanded);
          initialSnapshotRef.current = savedSnapshot;
        });
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? curCreateId : currentMode === 'copy' ? curCopyId : undefined,
            currentMode !== 'create' ? recordRef.current?.bookingNumber : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, stayData, detailLines, guests, linkedGuestId, sourceData, additionalData, availabilityResult, detailsExpanded, guestName, minimize, onClose, onRestoreModal, buildSnapshot,
      commercialLines, totalRoomsInput]);

  // Expose minimize to parent via ref
  useEffect(() => {
    if (minimizeRef) (minimizeRef as any).current = handleMinimize;
  }, [minimizeRef, handleMinimize]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {/* Loading overlay */}
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay, rgba(255,255,255,0.88))',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Saving booking...</span>
          </div>
        )}

        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            <IconSystem.calendar size={18} />
            {mode === 'edit' ? `Edit Booking — ${record?.bookingNumber || ''}` : mode === 'copy' ? 'Copy Booking' : 'New Booking'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Full-modal loading overlay — sits over the entire form (left panel +
            right summary) while the booking detail/guest/addon arrays load. The
            user sees a single centred spinner instead of a half-populated form. */}
        {loadingFullBooking && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              top: '3.5rem', // sits below the modal header
              background: 'var(--modal-loading-overlay)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 5,
              borderRadius: '0 0 inherit inherit',
              gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2.25rem',
                height: '2.25rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Loading booking details...
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Split-panel body */}
        <div className="booking-form-layout">
          {loadingFullBooking ? (
            // Reserve the panel space (so the overlay above has something to sit
            // on top of and the modal doesn't collapse to its header height),
            // but render no form content while loading.
            <div style={{ flex: 1, minHeight: '60vh' }} aria-hidden />
          ) : (
            <>
              {/* LEFT PANEL — Form sections */}
              <div className="booking-form-left">
                {/* Eligibility / override banner — projects the backend status decision
                    (AdvanceConfirmationRules.Resolve) so the cashier sees the result
                    before clicking Save. Yellow > Blue > Green > none. */}
                {eligibilityBanner && (
                  <div
                    style={{
                      marginBottom: '0.75rem',
                      padding: '0.625rem 0.875rem',
                      borderRadius: '6px',
                      fontSize: '0.8125rem',
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'flex-start',
                      ...(eligibilityBanner === 'override-loose'
                        ? { background: '#fef3c7', border: '1px solid #f59e0b', color: '#78350f' }
                        : eligibilityBanner === 'eligible-opted-in'
                          ? { background: '#dbeafe', border: '1px solid #3b82f6', color: '#1e3a8a' }
                          : { background: '#dcfce7', border: '1px solid #16a34a', color: '#14532d' }),
                    }}
                  >
                    <IconSystem.alert size={16} />
                    <div>
                      {eligibilityBanner === 'override-loose' && (
                        <>
                          <strong>Override needs approval.</strong> Booking will be saved as
                          {' '}<strong>PENDING_APPROVAL</strong>; provide remarks under Additional Details.
                        </>
                      )}
                      {eligibilityBanner === 'eligible-opted-in' && (
                        <>
                          <strong>Advance collected — normal workflow.</strong> Source is credit-eligible,
                          but an advance was entered. Booking will land as <strong>TENTATIVE</strong>
                          {' '}and auto-confirm when the advance is paid.
                        </>
                      )}
                      {eligibilityBanner === 'eligible-bypass' && (
                        <>
                          <strong>Credit-eligible source.</strong> Booking will be saved as
                          {' '}<strong>CONFIRMED</strong> without an advance. Balance settles later under the existing credit terms.
                        </>
                      )}
                    </div>
                  </div>
                )}

                <SourceChannelSection
                  data={sourceData}
                  onChange={updates => setSourceData(prev => ({ ...prev, ...updates }))}
                  fieldErrors={fieldErrors}
                />

                {/* UAT gap #1 (2026-08-01): stay dates + room lines are editable
                    until check-in. Only the PROPERTY stays locked in edit —
                    booking numbers are property-prefixed and inventory moves
                    across properties are a different feature. The backend
                    releases old inventory and consumes the new range with a
                    fresh availability check on every date/line change. */}
                <StayDetailsSection
                  data={stayData}
                  onChange={updates => setStayData(prev => ({ ...prev, ...updates }))}
                  fieldErrors={fieldErrors}
                  propertyLocked={mode === 'edit'}
                  // Edit only: a COPY creates a new booking, so the source
                  // booking's rooms must stay committed against availability.
                  editingBookingId={mode === 'edit' ? record?.bookingId : undefined}
                  backdatedMaxDays={backdatedMaxDays}
                  onAvailabilityResult={setAvailabilityResult}
                  checkingAvailability={checkingAvailability}
                  onCheckingChange={setCheckingAvailability}
                />
                {isBackdated && (
                  <div style={{
                    padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.8125rem',
                    margin: '0.25rem 0 0.5rem', color: 'var(--text-primary)',
                    background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.35)',
                  }}>
                    Backdated booking — check-in {stayData.checkInDate} is in the past
                    (allowed up to {backdatedMaxDays} day{backdatedMaxDays !== 1 ? 's' : ''} back).
                    Past nights consume inventory and the booking appears in the Yesterday arrivals queue.
                  </div>
                )}
                {editRepriceWarning && (
                  <div style={{
                    padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.8125rem',
                    margin: '0.25rem 0 0.5rem', color: 'var(--text-primary)',
                    background: 'rgba(37, 99, 235, 0.08)', border: '1px solid rgba(37, 99, 235, 0.3)',
                  }}>
                    {editRepriceWarning.replace('⚠ ', '')}
                  </div>
                )}

                <RoomRateSection
                  canOversell={allowedActions.includes('oversell')}
                  commercialLines={commercialLines}
                  onCommercialLinesChange={(lines) => {
                    setFieldErrors(prev => {
                      const n = { ...prev };
                      delete n.commercialLines; delete n.totalRooms; delete n.allocation;
                      return n;
                    });
                    setCommercialLines(lines);
                  }}
                  totalRooms={totalRoomsInput}
                  onTotalRoomsChange={(v) => {
                    setFieldErrors(prev => { const n = { ...prev }; delete n.totalRooms; delete n.allocation; return n; });
                    setTotalRoomsInput(v);
                  }}
                  availabilityResult={availabilityResult}
                  detailLines={detailLines}
                  onDetailLinesChange={setDetailLines}
                  numNights={numNights}
                  fieldErrors={fieldErrors}
                  checkingAvailability={checkingAvailability}
                  propertyId={stayData.propertyId}
                  checkInDate={stayData.checkInDate}
                  checkOutDate={stayData.checkOutDate}
                  childAges={childAges}
                  pricingModel={pricingModel}
                />

                <GuestSection
                  guests={guests}
                  onGuestsChange={setGuests}
                  linkedGuestId={linkedGuestId}
                  onLinkedGuestIdChange={setLinkedGuestId}
                  fieldErrors={fieldErrors}
                  bookingSource={sourceData.bookingSource}
                  childAgeLimit={detailLines[0]?.childAgeLimit}
                  infantAgeLimit={detailLines[0]?.infantAgeLimit}
                />

                {/* Order rationale: Discount drives the net total, which drives
                    the advance policy amount. Show Discount (with comp toggle)
                    BEFORE Advance so the cashier sees the dependency.
                    Additional Details (VIP / arrival / notes) is purely
                    informational and goes last. */}
                <DiscountSection
                  data={discountData}
                  onChange={updates => {
                    discountManuallyEditedRef.current = true;
                    setDiscountData(prev => ({ ...prev, ...updates }));
                  }}
                  fieldErrors={fieldErrors}
                  expanded={discountExpanded}
                  onToggle={() => setDiscountExpanded(prev => !prev)}
                  subtotal={subtotal}
                  isComplimentary={additionalData.isComplimentary}
                  complimentaryReason={additionalData.complimentaryReason}
                  onIsComplimentaryChange={(val) =>
                    setAdditionalData(prev => ({ ...prev, isComplimentary: val }))
                  }
                  onComplimentaryReasonChange={(val) =>
                    setAdditionalData(prev => ({ ...prev, complimentaryReason: val }))
                  }
                  requiresApproval={requiresApproval}
                  approvalReasons={approvalReasons}
                />

                {/* Ancillary add-ons — spa, flowers, etc. Charged in addition to
                    the room subtotal and NOT affected by discount. */}
                <AddonsSection
                  propertyId={stayData.propertyId}
                  rows={addons}
                  onChange={setAddons}
                  expanded={addonsExpanded}
                  onToggle={() => setAddonsExpanded(prev => !prev)}
                  checkInDate={stayData.checkInDate}
                  checkOutDate={stayData.checkOutDate}
                />

                <AdvanceSection
                  data={{
                    advanceDueDate: additionalData.advanceDueDate,
                    advanceRequired: additionalData.advanceRequired,
                    advanceOverrideRemarks: additionalData.advanceOverrideRemarks,
                  }}
                  onChange={(updates) => {
                    // Mark advance as manually edited so the auto-recalc effect
                    // (re-runs when discount/totals change) leaves it alone.
                    if ('advanceRequired' in updates || 'advanceDueDate' in updates) {
                      advanceManuallyEditedRef.current = true;
                    }
                    setAdditionalData(prev => ({ ...prev, ...updates }));
                  }}
                  fieldErrors={fieldErrors}
                  expanded={advanceExpanded}
                  onToggle={() => setAdvanceExpanded(prev => !prev)}
                  advanceConfig={advanceConfig}
                  netAmount={netAmount}
                  isComplimentary={additionalData.isComplimentary}
                  isSourceEligible={isSourceEligible}
                  policyAmount={policyAmount}
                  policyDueDate={policyDueDate}
                  isOverrideLoose={isOverrideLoose}
                />

                <AdditionalDetailsSection
                  data={additionalData}
                  onChange={updates => setAdditionalData(prev => ({ ...prev, ...updates }))}
                  fieldErrors={fieldErrors}
                  expanded={detailsExpanded}
                  onToggle={() => setDetailsExpanded(prev => !prev)}
                />
              </div>

              {/* RIGHT PANEL — Summary */}
              <div className="booking-form-right">
                <BookingSummaryPanel
                  commercialLines={commercialLines}
                  detailLines={detailLines}
                  numNights={numNights}
                  checkInDate={stayData.checkInDate}
                  checkOutDate={stayData.checkOutDate}
                  guestName={guestName}
                  childAges={childAges}
                  loading={loadingFullBooking}
                  pricingModel={pricingModel}
                  isComplimentary={additionalData.isComplimentary}
                  discountAmount={discountAmount}
              adjustmentPremium={adjustmentPremium}
              adjustmentDiscount={adjustmentDiscount}
                  netAmount={netAmount}
                  discountType={discountData.discountType || undefined}
                  discountValue={discountData.discountValue || undefined}
                  requiresApproval={requiresApprovalAny}
                  approvalReasons={
                    isOverrideLoose
                      ? `${approvalReasons ? approvalReasons + ' ' : ''}Advance override is looser than policy.`.trim()
                      : approvalReasons
                  }
                  addonLines={addons
                    .filter(a => a.addonId)
                    .map(a => ({
                      label: a.addonLabel || a.addonId,
                      quantity: Math.max(1, Number(a.quantity) || 1),
                      unitAmount: Math.max(0, Number(a.unitAmount) || 0),
                      lineAmount: Math.max(0, Number(a.unitAmount) || 0) * Math.max(1, Number(a.quantity) || 1),
                    }))}
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving || loadingFullBooking}>Cancel</button>
          {mode === 'edit' ? (
            <>
              {record && record.bookingStatusCode !== 'CANCELLED' && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setAdvanceModalOpen(true)}
                  disabled={isSaving || loadingFullBooking}
                  title="Record an advance payment for this booking"
                >
                  <IconSystem.dollarSign size={16} />
                  Advance
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={() => handleSave('confirm')}
                disabled={isSaving || loadingFullBooking || detailLines.length === 0 || !isDirty}
                title={!isDirty ? 'No changes to save' : undefined}
              >
                <IconSystem.save size={16} />
                {isSaving ? 'Saving...' : 'Update Booking'}
              </button>
            </>
          ) : (
            <>
              <button
                className="btn btn-secondary"
                onClick={() => handleSave('block')}
                disabled={isSaving || loadingFullBooking || detailLines.length === 0}
                title={`Save the booking and place a ${blockingConfig?.blockDurationHours ?? 24}-hour block on the inventory (auto-cancels if not confirmed).`}
              >
                {isSaving ? 'Saving...' : (requiresApprovalAny ? 'Save (Pending Approval)' : 'Save & Block')}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleSave('confirm')}
                disabled={isSaving || loadingFullBooking || detailLines.length === 0 || requiresApprovalAny}
                title={requiresApprovalAny ? 'This booking requires approval — it must be saved as Pending Approval.' : undefined}
              >
                <IconSystem.save size={16} />
                {isSaving ? 'Saving...' : 'Save & Confirm'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Save Confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit'
          ? 'Confirm Update'
          : requiresApprovalAny ? 'Save Pending Approval' : 'Confirm Booking'}
        message={
          backdatedWarning + editRepriceWarning + largeBookingWarning + (
          mode === 'edit'
            ? (requiresApprovalAny
                ? `This update will mark booking "${record?.bookingNumber}" as PENDING_APPROVAL${approvalReasons ? ` — ${approvalReasons}` : (isOverrideLoose ? ' — advance override is looser than policy' : '')}. Continue?`
                : `Save changes to booking "${record?.bookingNumber}"?`)
            : requiresApprovalAny
              ? `This booking will be saved as PENDING_APPROVAL${approvalReasons ? ` — ${approvalReasons}` : (isOverrideLoose ? ' — advance override is looser than policy' : '')}. An approver must review it before it can be confirmed.`
              : saveAsConfirmed
                ? 'Create this booking as CONFIRMED?'
                : saveAsBlocked
                  ? `Create this booking and place a ${blockingConfig?.blockDurationHours ?? 24}-hour BLOCK on the inventory? It will auto-cancel if not confirmed within the hold window.`
                  : eligibleBypass
                    ? 'Create this booking as CONFIRMED? Source is credit-eligible — no advance required, balance settles later.'
                    : 'Create this booking as TENTATIVE?'
          )
        }
        confirmLabel={
          mode === 'edit'
            ? (requiresApprovalAny ? 'Save Pending Approval' : 'Save')
            : requiresApprovalAny
              ? 'Save Pending Approval'
              : saveAsConfirmed || eligibleBypass
                ? 'Confirm Booking'
                : saveAsBlocked ? 'Save & Block' : 'Save'
        }
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <OversellAuthorizationDialog
        isOpen={oversellPrompt.open}
        message={oversellPrompt.message}
        submitting={isSaving}
        onConfirm={(reason) => {
          // One-shot authorisation: set, retry, clear. Cleared in a finally so a
          // later save in the same session can never inherit it.
          oversellAcceptedRef.current = { accepted: true, reason };
          setOversellPrompt({ open: false, message: '' });
          void Promise.resolve(handleConfirmSave()).finally(() => {
            oversellAcceptedRef.current = { accepted: false, reason: '' };
          });
        }}
        onCancel={() => {
          oversellAcceptedRef.current = { accepted: false, reason: '' };
          setOversellPrompt({ open: false, message: '' });
        }}
      />

      {/* Discard Confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      {/* Concurrency conflict — refresh from server */}
      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Record Modified by Another User"
        message={concurrencyConflict.message}
        confirmLabel="Load Latest"
        cancelLabel="Keep My Changes"
        variant="default"
        loading={isRefreshing}
        loadingMessage="Loading latest version..."
        onConfirm={handleRefreshFromConflict}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />

      {/* Availability refusal with no way forward for this user. Single button:
          there is nothing to confirm and nothing to reload — the form stays open
          with the edits intact so the dates, category or room count can be
          changed and the save retried. */}
      <ConfirmDialog
        open={availabilityBlock.open}
        title="Rooms not available"
        message={availabilityBlock.message}
        confirmLabel="OK"
        variant="danger"
        onConfirm={() => setAvailabilityBlock({ open: false, message: '' })}
      />

      {/* Advance Capture Modal — opened from the footer button (edit mode only) */}
      {mode === 'edit' && record && (
        <PaymentCaptureModal
          isOpen={advanceModalOpen}
          mode="create"
          collectionPoint="RESERVATIONS"
          bookingId={record.bookingId}
          propertyId={record.propertyId}
          bookingNumber={record.bookingNumber}
          suggestedAmount={
            // Default = outstanding advance (advanceRequired − advanceReceived),
            // not the full net balance. Cashier can edit if guest pays more.
            // Falls back to undefined when there's nothing outstanding.
            Math.max(0, (record.advanceRequired ?? 0) - (record.advanceReceived ?? 0)) || undefined
          }
          onSave={() => { setAdvanceModalOpen(false); /* parent grid re-fetches on its own refetch cycle */ }}
          onClose={() => setAdvanceModalOpen(false)}
          tabId={tabId}
          moduleName={moduleName}
        />
      )}
    </>
  );
};

export default BookingFormModal;
